/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useEffect, useMemo, useState } from "react";
import { useInvoiceTipsPostMutation } from "../../lib/react-query/useInvoiceTips";
import useInvoice from "./useInvoice";
import { extractErrorMessage } from "../../utils/api/apiErrors";

const DEFAULT_TIP_PERCENTAGES = [5, 10, 15];

export const useTipSelection = () => {
  const { invoice, setInvoice } = useInvoice();
  const { mutateAsync, error, isPending } = useInvoiceTipsPostMutation(invoice.id);
  const [visibleError, setVisibleError] = useState(null);

  useEffect(() => {
    const message = extractErrorMessage(error, null);

    if (message) {
      setVisibleError(message);

      const timer = setTimeout(() => {
        setVisibleError(null);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [error]);

  const totalAmount = useMemo(() => {
    if (!invoice) return 0;

    let amount = invoice.amount || 0;

    if (invoice.rest_amount) {
      amount += invoice.rest_amount;
    }

    if (invoice.tip && invoice.tip.amount) {
      amount -= invoice.tip.amount;
    }

    return amount;
  }, [invoice]);

  const tipPercentageValues = useMemo(() => {
    if (totalAmount <= 0.001) {
      return [];
    }

    return DEFAULT_TIP_PERCENTAGES.map((percentage) => ({
      value: Math.round(totalAmount * percentage / 100 * 100) / 100,
      percentage,
    }));
  }, [totalAmount]);

  const addTipsToInvoice = async (tipValue) => {
    const tipsData = {
      tips: 0,
      tips_amount: +tipValue,
    };

    await mutateAsync(tipsData, {
      onSuccess: (data) => {
        setInvoice(data);
      },
    });
  };

  return {
    tipPercentageValues,
    addTipsToInvoice,
    isPending,
    visibleError,
  };
};
